import { IBookmark, IBookmarkTag } from '../types/IBookmark';
import { DeduplicationService } from './DeduplicationService';

export interface IBookmarkService {
  createBookmark(bookmark: Partial<IBookmark>): Promise<{ id?: string } | undefined>;
  updateBookmark(bookmark: IBookmark): Promise<void>;
  updateTags(bookmarkId: string, tags: IBookmarkTag[], categories?: string[]): Promise<void>;
}

export class BookmarkService implements IBookmarkService {
  private storage: Storage;
  private BOOKMARKS_KEY = 'bookmarks';

  constructor() {
    this.storage = typeof window !== 'undefined' ? window.localStorage : ({} as Storage);
  }

  private getBookmarks(): IBookmark[] {
    const bookmarksStr = this.storage.getItem(this.BOOKMARKS_KEY);
    return bookmarksStr ? JSON.parse(bookmarksStr) : [];
  }

  private saveBookmarks(bookmarks: IBookmark[]): void {
    this.storage.setItem(this.BOOKMARKS_KEY, JSON.stringify(bookmarks));
  }

  private appendBookmarks(newBookmarks: IBookmark[]): void {
    const bookmarks = this.getBookmarks();
    this.saveBookmarks([...bookmarks, ...newBookmarks]);
  }

  async createBookmark(bookmarkData: Partial<IBookmark>): Promise<{ id?: string } | undefined> {
    try {
      const newBookmark: IBookmark = {
        id: Date.now().toString(), // Simple ID generation
        type: bookmarkData.type || 'url',
        url: bookmarkData.url || '',
        title: bookmarkData.title,
        description: bookmarkData.description,
        image: bookmarkData.image,
        source: bookmarkData.source || '',
        addedOn: Date.now(),
        tags: bookmarkData.tags || [],
        categories: bookmarkData.categories || [],
        viewCount: 0,
        clickCount: 0,
        is_starred: 'false',
        lastViewedOn: Date.now(),
      };

      this.appendBookmarks([newBookmark]);
      return { id: newBookmark.id };
    } catch (error) {
      console.error('Error creating bookmark:', error);
      return undefined;
    }
  }

  async updateBookmark(bookmark: IBookmark): Promise<void> {
    if (!bookmark.id) {
      throw new Error('Bookmark ID is required for update');
    }

    const bookmarks = this.getBookmarks();
    const index = bookmarks.findIndex(b => b.id === bookmark.id);

    if (index === -1) {
      throw new Error('Bookmark not found');
    }

    bookmarks[index] = {
      ...bookmarks[index],
      ...bookmark,
      lastViewedOn: Date.now(),
    };

    this.saveBookmarks(bookmarks);
  }

  async updateTags(bookmarkId: string, tags: IBookmarkTag[], categories?: string[]): Promise<void> {
    const bookmarks = this.getBookmarks();
    const bookmark = bookmarks.find(b => b.id === bookmarkId);

    if (!bookmark) {
      throw new Error('Bookmark not found');
    }

    bookmark.tags = tags;
    if (categories) {
      bookmark.categories = categories;
    }

    this.saveBookmarks(bookmarks);
  }

  async getBookmark(id: string): Promise<IBookmark | undefined> {
    const bookmarks = this.getBookmarks();
    return bookmarks.find(b => b.id === id);
  }

  async getAllBookmarks(): Promise<IBookmark[]> {
    return this.getBookmarks();
  }

  /**
   * Remove duplicate bookmarks based on URL
   */
  async deduplicateBookmarks(): Promise<{
    removed: number;
    remaining: number;
  }> {
    const bookmarks = this.getBookmarks();
    const deduplicated = DeduplicationService.deduplicateBookmarks(bookmarks);
    
    const removed = bookmarks.length - deduplicated.length;
    this.saveBookmarks(deduplicated);
    
    return {
      removed,
      remaining: deduplicated.length
    };
  }

  /**
   * Get duplicate statistics
   */
  async getDuplicateStats(): Promise<{
    total: number;
    unique: number;
    duplicates: number;
    duplicateUrls: string[];
  }> {
    const bookmarks = this.getBookmarks();
    return DeduplicationService.getDuplicateStats(bookmarks);
  }

  /**
   * Find duplicates without removing them
   */
  async findDuplicates(): Promise<{
    unique: IBookmark[];
    duplicates: IBookmark[];
  }> {
    const bookmarks = this.getBookmarks();
    return DeduplicationService.findDuplicates(bookmarks);
  }

  async deleteBookmark(id: string): Promise<void> {
    const bookmarks = this.getBookmarks();
    const filteredBookmarks = bookmarks.filter(b => b.id !== id);
    this.saveBookmarks(filteredBookmarks);
  }
}
